import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
// react component for creating dynamic tables
import ReactTable from "react-table";
// @material-ui/core components
import Checkbox from "@material-ui/core/Checkbox";
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/icons
import MailOutline from "@material-ui/icons/MailOutline";
import Check from "@material-ui/icons/Check";

// core components
import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import { agentApi } from "functions/apis";

const useStyles = makeStyles(styles);

export default function UserAvailableImpersonationList(props) {
  const source = useRef(new AbortController());

  const { id, reloadHandler, reload } = props;

  const [data, setData] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [filter, setFilter] = React.useState({
    searchString: ""
  });
  const [pagination, setPagination] = React.useState({
    pageIndex: 1,
    pageSize: 5
  });
  const [loading, setLoading] = React.useState(false);
  const [addedUser, setAddedUser] = React.useState({
    userID: id,
    agentIDs: []
  });

  const classes = useStyles();

  useEffect(() => {
    return () => source.current.abort();
  }, []);

  useEffect(() => {
    const handleUserChecked = (checked, user) => {
      if (checked) {
        setAddedUser({
          ...addedUser,
          agentIDs: [...addedUser.agentIDs, user.id]
        });
      } else {
        setAddedUser(addedUser.agentIDs.filter(x => x !== user.id));
      }
    };

    setLoading(true);
    agentApi
      .get(`/Impersonations/AvailableAgents/${id}`, {
        signal: source.current.signal,
        params: {
          filter,
          pagination
        }
      })
      .then(data => {
        setData(
          data.records.map(record => {
            return {
              ...record,
              actions: (
                <div className="actions-right">
                  <Checkbox
                    checked={addedUser.agentIDs.includes(record.id)}
                    onChange={e => handleUserChecked(e.target.checked, record)}
                    checkedIcon={<Check className={classes.checkedIcon} />}
                    icon={<Check className={classes.uncheckedIcon} />}
                    classes={{
                      checked: classes.checked,
                      root: classes.checkRoot
                    }}
                  />
                </div>
              )
            };
          })
        );
        setPages(Math.ceil(data.totalRecords / pagination.pageSize));
        setLoading(false);
      })
      .catch(err => console.log(err));
  }, [id, filter, pagination, reload, classes, addedUser]);

  const [disabled, setDisabled] = React.useState(false);
  const handleSubmit = e => {
    e.preventDefault();
    setDisabled(true);
    agentApi
      .post("/Impersonations", addedUser, {
        signal: source.current.signal
      })
      .then(() => {
        setAddedUser({
          userID: id,
          agentIDs: []
        });
        reloadHandler();
        setDisabled(false);
      })
      .catch(error => {
        console.log(error);
        setDisabled(false);
      });
  };

  const handleSearchStringChanged = e =>
    setFilter({
      ...filter,
      searchString: e.target.value === "" ? null : e.target.value
    });

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>AVAILABLE AGENTS</h4>
          </CardHeader>
          <CardBody>
            <GridItem xs={3}>
              <form onSubmit={handleSubmit}>
                <Button
                  disabled={disabled}
                  color="rose"
                  type="submit"
                  value="Submit"
                >
                  Add
                </Button>
              </form>
            </GridItem>
            <GridItem xs={3}>
              <CustomInput
                labelText="Search"
                formControlProps={{
                  fullWidth: true
                }}
                inputProps={{
                  onChange: handleSearchStringChanged,
                  value: filter.searchString === null ? "" : filter.searchString
                }}
              />
            </GridItem>
            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({
                  pageIndex: page + 1,
                  pageSize: pagination.pageSize
                })
              }
              onPageSizeChange={(pageSize, page) => {
                setPagination({ pageSize: pageSize, pageIndex: page + 1 });
              }}
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                {
                  Header: "Agent Code",
                  id: "agentCode",
                  accessor: d => d.code
                },
                {
                  Header: "Agent Name",
                  id: "agentName",
                  accessor: d => d.name
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

UserAvailableImpersonationList.propTypes = {
  id: PropTypes.string,
  reloadHandler: PropTypes.func,
  reload: PropTypes.object
};
